import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ISupplierAvailabilityException,
  ISupplierSchedule,
  SupplierAvailabilityStateEnum,
} from '../../../interfaces/supplier-schedule';
import { Message } from '../../../classes/Message';
import { Router } from '@angular/router';
import { MessageService } from 'primeng';
import { SupplierScheduleService } from '../../../services';
import { flatten } from '../../../utils/flatten';
import { Subject, from } from 'rxjs';
import { switchMap, debounceTime, finalize } from 'rxjs/operators';
import { ISupplierScheduleFilter } from '../../../interfaces/supplier-schedule-filter';
import moment from 'moment';

enum CalendarEventType {
  SupplierAvailability = 0,
  SupplierTask = 1,
  PastEvent = 2,
}

const stateConfig = {
  [SupplierAvailabilityStateEnum.unset]: {
    title: 'Unset',
    backgroundColor: '#FFBF00',
    className: 'icon-unknown',
  },
  [SupplierAvailabilityStateEnum.unavailable]: {
    title: 'Unavailable',
    backgroundColor: '#e0284f',
    className: 'icon-unavailable',
  },
  [SupplierAvailabilityStateEnum.available]: {
    title: 'Available',
    backgroundColor: '#4CAF50',
    className: 'icon-available',
  },
  [SupplierAvailabilityStateEnum.doNotDisturb]: {
    title: 'Do Not Disturb',
    backgroundColor: '#e0284f',
    className: 'icon-dnd',
  },
};

@Component({
  selector: 'supplier-availability-page',
  templateUrl: './supplier-availability-view.component.html',
  styleUrls: ['./supplier-availability-view.component.scss'],
})
export class SupplierAvailabilityViewComponent implements OnInit, OnDestroy {
  public supplierSchedule: ISupplierSchedule;
  isLoading: boolean = false;
  availabilityFilterVal: ISupplierScheduleFilter;
  supplierId: number = 0;
  events: any[] = [];
  private calendarViewChangedSubject = new Subject<ISupplierScheduleFilter>();
  showRangeSelectionDialog: boolean = false;
  selectedDateRangeStart: Date;
  selectedDateRangeEnd: Date;
  selectedDateRangeEndDisplay: Date;
  availabilityStates = SupplierAvailabilityStateEnum;

  constructor(
    private scheduleService: SupplierScheduleService,
    public messageService: MessageService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.initSupplierAvailabilityFilter();
    this.setupCalendarSubscription();
  }

  private setupCalendarSubscription() {
    this.calendarViewChangedSubject
      .pipe(
        switchMap((filter) => {
          this.isLoading = true;
          return from(this.scheduleService.getSupplierAvailability(flatten(filter)));
        }),
        debounceTime(500),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe(
        (result) => {
          if (result) {
            this.supplierSchedule = result;

            //console.log('* Supplier Schedule: ', result);

            this.setEvents(result);
          } else {
            // notify we could not fetch a valid data for supplier
            //this.calendarConfiguration = {editable: false};
            this.messageService.clear();
            this.messageService.add(
              Message.Error(
                'We were unable to fetch a valid schedule for your account',
                'Invalid Request',
              ),
            );
          }
        },
        (err) => {
          this.messageService.add(Message.Error('Error loading data', 'Request Error'));
        },
        () => {
          this.isLoading = false;
        },
      );
  }

  private initSupplierAvailabilityFilter() {
    this.availabilityFilterVal = {
      first: 0,
      rows: 31,
      sortField: '',
      sortOrder: 1,
      filters: {
        fromDate: { value: moment().startOf('month').toDate() },
        toDate: { value: moment().endOf('month').toDate() },
        supplierId: { value: this.supplierId },
        dayOrder: { value: null },
        orderPickup: { value: null },
        scheduleSaturation: { value: 0 },
        contractTypeIds: { value: null },
        organisationTagIds: { value: null },
        serviceDeliveryGroupIds: { value: null },
        engineiusRegionIds: { value: null },
        engineiusAreaIds: { value: null },
      },
    };
  }

  private getNextState(currentState: SupplierAvailabilityStateEnum): SupplierAvailabilityStateEnum {
    const transitions = {
      [SupplierAvailabilityStateEnum.unset]: SupplierAvailabilityStateEnum.available,
      [SupplierAvailabilityStateEnum.available]: SupplierAvailabilityStateEnum.unavailable,
      [SupplierAvailabilityStateEnum.unavailable]: SupplierAvailabilityStateEnum.doNotDisturb,
      [SupplierAvailabilityStateEnum.doNotDisturb]: SupplierAvailabilityStateEnum.available, // unset maybe?
    };
    return transitions[currentState];
  }

  setEvents(supplierSchedule: ISupplierSchedule) {
    const startDate = moment(this.availabilityFilterVal.filters.fromDate.value);
    const endDate = moment(this.availabilityFilterVal.filters.toDate.value);
    const durationDays = Math.abs(moment.duration(startDate.diff(endDate)).asDays());

    const schedule = Array(durationDays)
      .fill(null)
      .map((_, index) => {
        const currentDate = moment.utc(startDate).add(index, 'days');
        const exception = supplierSchedule.supplierAvailabilityExceptions.find((ex) =>
          moment(ex.exceptionDate).isSame(currentDate, 'day'),
        );

        const state = exception?.availabilityState ?? SupplierAvailabilityStateEnum.unset;
        const config = stateConfig[state];

        return {
          title: config.title,
          start: currentDate.startOf('day').toDate(),
          end: currentDate.endOf('day').toDate(),
          backgroundColor: config.backgroundColor,
          className: `${currentDate.isBefore(moment()) ? 'in-past ' : ''}${config.className}`,
          eventId: exception?.id ?? -1,
          allDay: true,
          jobId: null,
          calendarEventType: currentDate.isSameOrAfter(moment(), 'day')
            ? CalendarEventType.SupplierAvailability
            : CalendarEventType.PastEvent,
          state,
          extendedProps: {
            type: state,
          },
        };
      });

    const supplierTasks = supplierSchedule.supplierTasks.map((task) => ({
      title: `${task.jobNumber} - ${task.regNum}`,
      start: moment
        .utc(task.taskSupplierSchedule?.specifiedPickUpTime ?? task.dropoffLatestDateTime)
        .toDate(),
      end: moment
        .utc(task.taskSupplierSchedule?.specifiedDropOffTime ?? task.dropoffLatestDateTime)
        .toDate(),
      backgroundColor: 'grey',
      className: '',
      eventId: -1,
      allDay: false,
      jobId: task.jobId,
      calendarEventType: CalendarEventType.SupplierTask,
    }));

    this.events = [...schedule, ...supplierTasks];
  }

  calendarViewChanged(view: any) {
    this.availabilityFilterVal.filters.fromDate.value = view.currentStart;
    this.availabilityFilterVal.filters.toDate.value = view.currentEnd;
    this.calendarViewChangedSubject.next(this.availabilityFilterVal);
  }

  calendarVisibleDatesChanged(visibleDates: any) {
    this.availabilityFilterVal.filters.fromDate.value = visibleDates.start;
    this.availabilityFilterVal.filters.toDate.value = visibleDates.end;
    this.calendarViewChangedSubject.next(this.availabilityFilterVal);
  }

  calendarDayHeaderSelected(day: number) {
    if (!this.supplierSchedule) return;

    const startDate = moment(this.availabilityFilterVal.filters.fromDate.value);
    const endDate = moment(this.availabilityFilterVal.filters.toDate.value);

    while (startDate.isSameOrBefore(endDate)) {
      if (startDate.day() === day) {
        const existingExceptionIndex =
          this.supplierSchedule.supplierAvailabilityExceptions.findIndex((ex) =>
            moment(ex.exceptionDate).isSame(startDate, 'day'),
          );

        if (existingExceptionIndex > -1) {
          const currentState =
            this.supplierSchedule.supplierAvailabilityExceptions[existingExceptionIndex]
              .availabilityState;
          this.supplierSchedule.supplierAvailabilityExceptions[
            existingExceptionIndex
          ].availabilityState = this.getNextState(currentState);
        } else {
          this.supplierSchedule.supplierAvailabilityExceptions.push({
            availabilityState: SupplierAvailabilityStateEnum.unavailable,
            exceptionDate: startDate.clone().utc().toDate(),
            id: -1,
          });
        }
      }
      startDate.add(1, 'day');
    }

    this.save().then(() => this.calendarViewChangedSubject.next(this.availabilityFilterVal));
  }

  calendarDateClicked(dateRange: any) {
    if (!this.supplierSchedule) return;

    const startDate = moment(dateRange[0].start);
    const endDate = moment(dateRange[0].end);
    const durationDays = Math.abs(moment.duration(startDate.diff(endDate)).asDays());

    if (durationDays > 1) {
      this.handleDateRangeSelection(startDate, endDate);
    } else {
      this.handleSingleDateSelection(startDate);
    }
  }

  private handleDateRangeSelection(startDate: moment.Moment, endDate: moment.Moment) {
    this.selectedDateRangeStart = startDate.toDate();
    this.selectedDateRangeEnd = endDate.toDate();
    this.selectedDateRangeEndDisplay = endDate.subtract(1, 'second').toDate();
    this.showRangeSelectionDialog = true;
  }

  private handleSingleDateSelection(date: moment.Moment) {
    const existingExceptionIndex = this.supplierSchedule.supplierAvailabilityExceptions.findIndex(
      (ex) => moment(ex.exceptionDate).isSame(date, 'day'),
    );

    console.log('WOOWE)OWEOWOIWO');

    if (existingExceptionIndex > -1) {
      const currentState =
        this.supplierSchedule.supplierAvailabilityExceptions[existingExceptionIndex]
          .availabilityState;
      const nextState = this.getNextState(currentState);

      if (nextState === SupplierAvailabilityStateEnum.unset) {
        this.supplierSchedule.supplierAvailabilityExceptions.splice(existingExceptionIndex, 1);
      } else {
        this.supplierSchedule.supplierAvailabilityExceptions[
          existingExceptionIndex
        ].availabilityState = nextState;
      }
    } else {
      this.supplierSchedule.supplierAvailabilityExceptions.push({
        availabilityState: SupplierAvailabilityStateEnum.unavailable,
        exceptionDate: date.utc().toDate(),
        id: -1,
      });
    }

    this.save().then(() => this.calendarViewChangedSubject.next(this.availabilityFilterVal));
  }

  setDateRangeAvailability(state: SupplierAvailabilityStateEnum) {
    const durationDays = Math.abs(
      moment.duration(moment(this.selectedDateRangeStart).diff(this.selectedDateRangeEnd)).asDays(),
    );

    Array(durationDays)
      .fill(null)
      .forEach((_, index) => {
        const currentDate = moment(this.selectedDateRangeStart).add(index, 'days');
        const existingIndex = this.supplierSchedule.supplierAvailabilityExceptions.findIndex((ex) =>
          moment(ex.exceptionDate).isSame(currentDate, 'day'),
        );

        if (existingIndex > -1) {
          if (state === SupplierAvailabilityStateEnum.unset) {
            this.supplierSchedule.supplierAvailabilityExceptions.splice(existingIndex, 1);
          } else {
            this.supplierSchedule.supplierAvailabilityExceptions[existingIndex].availabilityState =
              state;
          }
        } else if (state !== SupplierAvailabilityStateEnum.unset) {
          this.supplierSchedule.supplierAvailabilityExceptions.push({
            availabilityState: state,
            exceptionDate: moment.utc(currentDate).toDate(),
            id: -1,
          });
        }
      });

    this.showRangeSelectionDialog = false;
    this.save().then(() => this.calendarViewChangedSubject.next(this.availabilityFilterVal));
  }

  calendarEventChanged(event: any) {
    const { calendarEventType, jobId } = event.event.extendedProps;

    if (calendarEventType === CalendarEventType.SupplierAvailability) {
      this.handleAvailabilityEventChange(event.event);
    } else if (calendarEventType === CalendarEventType.SupplierTask && jobId) {
      this.router.navigate([]).then(() => {
        window.open(`app/jobscreen/${jobId}`, '_blank');
      });
    }
  }

  private handleAvailabilityEventChange(event: any) {
    const currentState = event.extendedProps.state;
    const nextState = this.getNextState(currentState);

    // cannot set DND or Unavailable as the state when there are tasks on the date
    if (
      (nextState == SupplierAvailabilityStateEnum.doNotDisturb ||
        nextState == SupplierAvailabilityStateEnum.unavailable) &&
      this.supplierSchedule.supplierTasks.some((x) => {
        const taskStart = moment.utc(
          x.taskSupplierSchedule?.specifiedPickUpTime ?? x.dropoffLatestDateTime,
        );
        const taskEnd = moment.utc(
          x.taskSupplierSchedule?.specifiedDropOffTime ?? x.dropoffLatestDateTime,
        );
        return moment(event.start).isBetween(taskStart, taskEnd, 'day', '[]');
      })
    ) {
      this.messageService.clear();
      this.messageService.add(
        Message.Error('Cannot change availability as it would conflict with assigned tasks'),
      );
      return;
    }

    const existingIndex = this.supplierSchedule.supplierAvailabilityExceptions.findIndex(
      (ex) => ex.id === event.eventId && moment(ex.exceptionDate).isSame(event.start, 'day'),
    );

    if (existingIndex > -1) {
      if (nextState === SupplierAvailabilityStateEnum.unset) {
        this.supplierSchedule.supplierAvailabilityExceptions.splice(existingIndex, 1);
      } else {
        this.supplierSchedule.supplierAvailabilityExceptions[existingIndex].availabilityState =
          nextState;
      }
    } else if (nextState !== SupplierAvailabilityStateEnum.unset) {
      this.supplierSchedule.supplierAvailabilityExceptions.push({
        availabilityState: nextState,
        exceptionDate: moment.utc(event.start).toDate(),
        id: -1,
      });
    }

    this.save().then(() => this.calendarViewChangedSubject.next(this.availabilityFilterVal));
  }

  async save() {
    const exceptions = this.supplierSchedule.supplierAvailabilityExceptions.map((ex) => ({
      availabilityState: ex.availabilityState,
      exceptionDate: moment.utc(ex.exceptionDate).toDate(),
      id: ex.id,
    }));

    const availability: ISupplierSchedule = <ISupplierSchedule>{
      supplierId: this.supplierSchedule.id,
      supplierAvailabilityExceptions: exceptions,
      validity: this.availabilityFilterVal.filters.toDate.value,
      fromDateTime: this.availabilityFilterVal.filters.fromDate.value,
      toDateTime: this.availabilityFilterVal.filters.toDate.value,
    };

    try {
      const res = await this.scheduleService.setSupplierAvailability(availability);
      if (res) {
        this.messageService.clear();
        this.messageService.add(Message.Success('Your availability information has been updated'));
      }
      return res;
    } catch (error) {
      this.messageService.add(Message.Error('Failed to update availability'));
      return false;
    }
  }

  done() {
    if (!this.supplierSchedule) return false;

    this.save().then((res) => {
      if (res) {
        setTimeout(() => {
          this.router.navigate(['/app']);
        }, 2500);
      }
    });
  }

  ngOnDestroy() {
    this.calendarViewChangedSubject.complete();
  }
}
